import React, { useState, useEffect } from "react";
import {
  Card,
  Table,
  Tag,
  Space,
  Button,
  Typography,
  Row,
  Col,
  Statistic,
  Select,
  DatePicker,
  Input,
  Modal,
  Form,
  InputNumber,
  message,
  Dropdown,
  theme,
} from "antd";
import {
  DollarOutlined,
  CheckCircleOutlined,
  FileExcelOutlined,
  FilePdfOutlined,
  DownOutlined,
  EditOutlined,
  PlusOutlined,
} from "@ant-design/icons";
import PageLayout from "@components/pageLayout/PageLayout.react";
import { authenticatedFetch } from "@/libs/api";
import { makePagination } from "@components/common/TablePagination";
import dayjs from "dayjs";

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

// Sharp, tech-style input
const inputStyle = { borderRadius: 2 };

export default function OutstandingReceivable() {
  const { token } = theme.useToken();
  const [loading, setLoading] = useState(true);
  const [selectedGroup, setSelectedGroup] = useState("all");
  const [markModalOpen, setMarkModalOpen] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [form] = Form.useForm();
  const [pageSize, setPageSize] = useState(10);

  const [groupOptions, setGroupOptions] = useState([
    { value: "all", label: "All Control Groups" },
  ]);
  const [receivableData, setReceivableData] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        // Fetch company ledger entries for outstanding receivable
        const res = await authenticatedFetch("/api/settlements/ledger/company?limit=200");
        if (res.ok) {
          const json = await res.json();
          if (json.r === 0 && json.data) {
            const entries = json.data.map((e, i) => {
              const credit = Number(e.credit) || 0;
              const debit = Number(e.debit) || 0;
              const closingBalance = Number(e.closing_balance) || 0;
              let status = "pending";
              if (debit >= credit && credit > 0) status = "settled";
              else if (debit > 0) status = "partial";

              return {
                id: `RCV-${String(i + 1).padStart(3, "0")}`,
                groupId: e.company_id || "",
                groupName: e.company_id || "Group",
                periodStart: e.date,
                periodEnd: e.date,
                dispatchCount: 0,
                totalKwh: 0,
                calculatedAmount: credit,
                invoiceNumber: null,
                invoicedAmount: credit > 0 ? credit : null,
                receivedAmount: debit > 0 ? debit : null,
                status,
              };
            });
            setReceivableData(entries);

            // Build group options from unique company_ids
            const uniqueGroups = [...new Set(json.data.map((e) => e.company_id).filter(Boolean))];
            setGroupOptions([
              { value: "all", label: "All Control Groups" },
              ...uniqueGroups.map((g) => ({ value: g, label: g })),
            ]);
          }
        }
      } catch (error) {
        console.error("Failed to fetch outstanding receivable:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Filter by group
  const filteredData = selectedGroup === "all"
    ? receivableData
    : receivableData.filter((r) => r.groupId === selectedGroup);

  // Calculate stats (exclude settled)
  const outstandingData = filteredData.filter((r) => r.status !== "settled");
  const totalOutstanding = outstandingData.reduce((sum, r) => sum + r.calculatedAmount, 0);
  const totalInvoiced = outstandingData
    .filter((r) => r.invoicedAmount)
    .reduce((sum, r) => sum + r.invoicedAmount, 0);
  const totalReceived = outstandingData
    .filter((r) => r.receivedAmount)
    .reduce((sum, r) => sum + r.receivedAmount, 0);
  const pendingCount = outstandingData.filter((r) => r.status === "pending").length;

  const handleMarkInvoice = (record) => {
    setSelectedRecord(record);
    form.setFieldsValue({
      invoiceNumber: record.invoiceNumber || "",
      invoicedAmount: record.invoicedAmount || record.calculatedAmount,
      receivedAmount: record.receivedAmount || 0,
    });
    setMarkModalOpen(true);
  };

  const handleSaveInvoice = (values) => {
    message.success(`Invoice ${values.invoiceNumber} marked for ${selectedRecord.groupName}`);
    setMarkModalOpen(false);
    form.resetFields();
  };

  const getStatusTag = (status) => {
    const config = {
      pending: { color: "default", text: "Pending" },
      invoiced: { color: "processing", text: "Invoiced" },
      partial: { color: "warning", text: "Partial" },
      settled: { color: "success", text: "Settled" },
    };
    const { color, text } = config[status] || config.pending;
    return <Tag color={color}>{text}</Tag>;
  };

  const exportMenuItems = [
    { key: "excel", label: "Export to Excel", icon: <FileExcelOutlined /> },
    { key: "csv", label: "Export to CSV", icon: <FileExcelOutlined /> },
    { key: "pdf", label: "Export to PDF", icon: <FilePdfOutlined /> },
  ];

  const handleExport = ({ key }) => {
    message.success(`Exporting to ${key.toUpperCase()}...`);
  };

  const columns = [
    {
      title: "Control Group",
      dataIndex: "groupName",
      key: "groupName",
      render: (name, record) => (
        <Space direction="vertical" size={0}>
          <Text strong>{name}</Text>
          <Text type="secondary" style={{ fontSize: 12 }}>{record.groupId}</Text>
        </Space>
      ),
    },
    {
      title: "Period",
      key: "period",
      render: (_, record) => (
        <Text>{record.periodStart} ~ {record.periodEnd}</Text>
      ),
    },
    {
      title: "Dispatches",
      dataIndex: "dispatchCount",
      key: "dispatchCount",
      align: "right",
    },
    {
      title: "Total kWh",
      dataIndex: "totalKwh",
      key: "totalKwh",
      align: "right",
      render: (kwh) => kwh.toLocaleString(),
    },
    {
      title: "Calculated Amount",
      dataIndex: "calculatedAmount",
      key: "calculatedAmount",
      align: "right",
      render: (amount) => (
        <Text strong style={{ color: token.colorPrimary }}>
          ${amount.toLocaleString(undefined, { minimumFractionDigits: 2 })}
        </Text>
      ),
    },
    {
      title: "Invoice #",
      dataIndex: "invoiceNumber",
      key: "invoiceNumber",
      render: (inv) => inv || <Text type="secondary">-</Text>,
    },
    {
      title: "Invoiced",
      dataIndex: "invoicedAmount",
      key: "invoicedAmount",
      align: "right",
      render: (amount) => amount
        ? `$${amount.toLocaleString(undefined, { minimumFractionDigits: 2 })}`
        : <Text type="secondary">-</Text>,
    },
    {
      title: "Received",
      dataIndex: "receivedAmount",
      key: "receivedAmount",
      align: "right",
      render: (amount) => amount
        ? `$${amount.toLocaleString(undefined, { minimumFractionDigits: 2 })}`
        : <Text type="secondary">-</Text>,
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status) => getStatusTag(status),
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <Space>
          {record.status !== "settled" && (
            <Button
              type="link"
              size="small"
              icon={<EditOutlined />}
              onClick={() => handleMarkInvoice(record)}
            >
              {record.status === "pending" ? "Mark Invoice" : "Update"}
            </Button>
          )}
          {record.status === "invoiced" && (
            <Button
              type="link"
              size="small"
              icon={<CheckCircleOutlined />}
              onClick={() => message.success("Marked as settled")}
            >
              Settle
            </Button>
          )}
        </Space>
      ),
    },
  ];

  return (
    <PageLayout active="outstandingReceivable">
      {/* Page Header */}
      <div style={{ marginBottom: 24 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
          <div>
            <Title level={2} style={{ marginBottom: 4 }}>
              Outstanding Receivable
            </Title>
            <Text type="secondary" style={{ fontSize: 16 }}>
              Track and manage outstanding receivables by control group
            </Text>
          </div>
          <Dropdown menu={{ items: exportMenuItems, onClick: handleExport }}>
            <Button size="large" style={inputStyle}>
              Export <DownOutlined />
            </Button>
          </Dropdown>
        </div>
      </div>

      {/* Filters */}
      <Card bordered={false} style={{ marginBottom: 24, borderRadius: 12 }}>
        <Space wrap size="middle">
          <div>
            <Text type="secondary" style={{ display: "block", marginBottom: 8 }}>
              Control Group
            </Text>
            <Select
              value={selectedGroup}
              onChange={setSelectedGroup}
              options={groupOptions}
              style={{ width: 200, ...inputStyle }}
              size="large"
            />
          </div>
        </Space>
      </Card>

      {/* Stats Cards */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={12} sm={6}>
          <Card bordered={false} style={{ borderRadius: 12 }}>
            <Statistic
              title="Total Outstanding"
              value={totalOutstanding}
              precision={2}
              prefix="$"
              valueStyle={{ color: token.colorPrimary }}
            />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card bordered={false} style={{ borderRadius: 12 }}>
            <Statistic
              title="Total Invoiced"
              value={totalInvoiced}
              precision={2}
              prefix="$"
              valueStyle={{ color: "#f5a623" }}
            />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card bordered={false} style={{ borderRadius: 12 }}>
            <Statistic
              title="Total Received"
              value={totalReceived}
              precision={2}
              prefix="$"
              valueStyle={{ color: token.colorSuccess }}
            />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card bordered={false} style={{ borderRadius: 12 }}>
            <Statistic
              title="Pending Invoices"
              value={pendingCount}
              valueStyle={{ color: "#faad14" }}
            />
          </Card>
        </Col>
      </Row>

      {/* Receivables Table */}
      <Card
        bordered={false}
        style={{ borderRadius: 12 }}
        title={<Text strong>Outstanding Receivables</Text>}
      >
        <Table
          columns={columns}
          dataSource={filteredData}
          rowKey="id"
          loading={loading}
          pagination={makePagination(pageSize, setPageSize)}
        />
      </Card>

      {/* Mark Invoice Modal */}
      <Modal
        title={selectedRecord ? `Mark Invoice - ${selectedRecord.groupName}` : "Mark Invoice"}
        open={markModalOpen}
        onCancel={() => setMarkModalOpen(false)}
        footer={null}
      >
        <Form form={form} layout="vertical" onFinish={handleSaveInvoice}>
          <Form.Item
            name="invoiceNumber"
            label="Invoice Number"
            rules={[{ required: true, message: "Invoice number is required" }]}
          >
            <Input placeholder="INV-2026-XXXX" style={inputStyle} />
          </Form.Item>
          <Form.Item
            name="invoicedAmount"
            label="Invoiced Amount"
            rules={[{ required: true, message: "Amount is required" }]}
          >
            <InputNumber
              prefix="$"
              style={{ width: "100%", ...inputStyle }}
              precision={2}
              min={0}
            />
          </Form.Item>
          <Form.Item
            name="receivedAmount"
            label="Received Amount"
          >
            <InputNumber
              prefix="$"
              style={{ width: "100%", ...inputStyle }}
              precision={2}
              min={0}
            />
          </Form.Item>
          <Form.Item style={{ marginBottom: 0, marginTop: 24 }}>
            <Space>
              <Button type="primary" htmlType="submit" style={inputStyle}>
                Save
              </Button>
              <Button onClick={() => setMarkModalOpen(false)} style={inputStyle}>
                Cancel
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </PageLayout>
  );
}
